"use client";

import { useState } from "react";
import {
  CopyButton,
  Notice,
  StatCard,
  StatGrid,
  TextField,
} from "@/components/tool-kit/primitives";
import {
  BUTTON_SECONDARY,
  RESULT_CARD,
  WIDGET_H3,
} from "@/components/tool-kit/styles";
import type { LogisticStrings } from "@/components/tools/logistic-regression-calculator";
import {
  Explanation,
  Formula,
  ResultTable,
  WorkingPanel,
} from "@/components/tools/stats-descriptive/ui";
import { XYPlot } from "@/components/tools/stats-regression/xy-plot";
import { t } from "@/lib/i18n/interpolate";
import { parseNumberInput } from "@/lib/parse-number";
import {
  formatPRelation,
  formatPValue,
  formatStat,
  type NumberFormatter,
} from "@/lib/tool-kit/stats-descriptive/format";
import type { Design } from "@/lib/tool-kit/stats-regression/design";
import {
  classify,
  type LogisticReport,
  predictProbability,
} from "@/lib/tool-kit/stats-regression/logistic";
import {
  apaBounded,
  apaPClause,
  fixed,
} from "@/lib/tool-kit/stats-research/format";

interface Props {
  report: LogisticReport;
  design: Design;
  termNames: string[];
  outcomeName: string;
  event: string;
  cutoff: number;
  strings: LogisticStrings;
  fmt: NumberFormatter;
}

const EVP_LIMIT = 10;
const CURVE_POINTS = 80;
const BIG = 1e6;

/** A number for a table cell, in scientific notation when it is too large to read. */
function big(fmt: NumberFormatter, value: number): string {
  if (Number.isFinite(value) && Math.abs(value) >= BIG) {
    return fmt(value, { notation: "scientific", maximumSignificantDigits: 3 });
  }
  return formatStat(fmt, value);
}

function interval(
  low: number,
  high: number,
  fmt: NumberFormatter,
  strings: LogisticStrings
): string {
  return t(strings.ciTemplate, { low: big(fmt, low), high: big(fmt, high) });
}

function Banners({ report, strings, fmt }: Props) {
  return (
    <div className="space-y-3">
      {report.status === "separation" ? (
        <div
          className="space-y-2 rounded-lg border border-destructive/40 bg-destructive/5 p-4"
          role="alert"
        >
          <p className="font-semibold text-destructive">
            {strings.separationTitle}
          </p>
          <p className="text-foreground text-sm leading-relaxed">
            {strings.separation}
          </p>
        </div>
      ) : null}
      {report.status === "no-convergence" && !report.firth ? (
        <Notice>{strings.noConvergence}</Notice>
      ) : null}
      {report.firth ? <Notice>{strings.firthInfo}</Notice> : null}
      {report.firth && !report.converged ? (
        <Notice>{strings.firthConverged}</Notice>
      ) : null}
      {report.eventsPerVariable < EVP_LIMIT ? (
        <Notice>
          {t(strings.evpWarning, {
            events: fmt(report.eventsPerVariable, { maximumFractionDigits: 1 }),
          })}
        </Notice>
      ) : null}
    </div>
  );
}

function equation(
  report: LogisticReport,
  termNames: string[],
  event: string,
  fmt: NumberFormatter,
  strings: LogisticStrings
): string {
  let text = t(strings.equationIntercept, {
    event,
    a: big(fmt, report.coefficients[0].estimate),
  });
  for (const [j, name] of termNames.entries()) {
    const row = report.coefficients[j + 1];
    if (!row.aliased) {
      text += t(
        row.estimate < 0 ? strings.equationMinus : strings.equationPlus,
        {
          b: big(fmt, Math.abs(row.estimate)),
          name,
        }
      );
    }
  }
  return text;
}

function apaSummary(props: Props): string {
  const { report, termNames, outcomeName, strings, fmt } = props;
  const levelText = fmt(report.level, { style: "percent" });
  const significant = report.lrP < 0.05;
  const model = t(strings.apaModel, {
    y: outcomeName,
    predictors: termNames.join(strings.predictorSeparator),
    verdict: significant ? strings.apaVerdictSig : strings.apaVerdictNot,
    df: fmt(report.lrDf),
    n: fmt(report.n),
    chi: fixed(fmt, report.lrChi2, 2),
    p: apaPClause(fmt, report.lrP),
    r2: apaBounded(fmt, report.nagelkerke, 2),
  });
  if (report.status === "separation") {
    return model;
  }
  const terms = report.coefficients.slice(1).map((row, j) =>
    t(strings.apaTerm, {
      name: termNames[j],
      or: fixed(fmt, row.oddsRatio, 2),
      level: levelText,
      low: fixed(fmt, row.orLow, 2),
      high: fixed(fmt, row.orHigh, 2),
      p: apaPClause(fmt, row.p),
    })
  );
  return [model, ...terms].join("\n");
}

function Headline(props: Props) {
  const { report, termNames, outcomeName, event, cutoff, strings, fmt } = props;
  const num = (value: number) => formatStat(fmt, value);
  const confusion = classify(props.design.y, report.fitted, cutoff);
  const apa = apaSummary(props);
  return (
    <div aria-live="polite" className="space-y-4">
      <div className={`${RESULT_CARD} text-center`}>
        <p className="text-muted-foreground text-sm">{strings.equationTitle}</p>
        <p className="break-words font-mono font-semibold text-foreground text-lg">
          {equation(report, termNames, event, fmt, strings)}
        </p>
      </div>
      <StatGrid>
        <StatCard
          emphasis
          label={t(report.firth ? strings.lrChi2Firth : strings.lrChi2, {
            df: fmt(report.lrDf),
          })}
          value={num(report.lrChi2)}
        />
        <StatCard label={strings.lrP} value={formatPValue(fmt, report.lrP)} />
        <StatCard label={strings.mcFadden} value={num(report.mcFadden)} />
        <StatCard label={strings.nagelkerke} value={num(report.nagelkerke)} />
        <StatCard label={strings.auc} value={num(report.auc)} />
        <StatCard
          label={strings.accuracy}
          value={fmt(confusion.accuracy, {
            style: "percent",
            maximumFractionDigits: 1,
          })}
        />
      </StatGrid>
      <Explanation>
        {t(report.lrP < 0.05 ? strings.summary : strings.summaryNot, {
          y: outcomeName,
          event,
          p: formatPRelation(fmt, report.lrP),
          auc: num(report.auc),
        })}
      </Explanation>
      <Explanation>
        {t(strings.casesValue, {
          n: fmt(report.n),
          events: fmt(report.events),
          nonEvents: fmt(report.nonEvents),
        })}
      </Explanation>
      <div className="space-y-2">
        <p className="font-medium text-foreground text-sm">
          {strings.apaTitle}
        </p>
        <p className="whitespace-pre-line font-mono text-foreground text-sm">
          {apa}
        </p>
        <CopyButton
          copiedLabel={strings.copied}
          label={strings.copyResult}
          small
          text={apa}
        />
      </div>
    </div>
  );
}

function Coefficients({ report, termNames, strings, fmt }: Props) {
  const levelText = fmt(report.level, { style: "percent" });
  const unreliable = report.status === "separation";
  const cell = (aliased: boolean, text: string) =>
    aliased ? strings.aliasedTerm : text;
  /** Odds ratios mean nothing when the likelihood has no maximum. */
  const orCell = (aliased: boolean, text: string) =>
    unreliable && !aliased ? "—" : cell(aliased, text);
  const rows = report.coefficients.map((row, j) => [
    j === 0 ? strings.termIntercept : termNames[j - 1],
    cell(row.aliased, big(fmt, row.estimate)),
    cell(row.aliased, big(fmt, row.se)),
    cell(row.aliased, formatStat(fmt, row.z, 2)),
    orCell(row.aliased, formatPValue(fmt, row.p)),
    orCell(row.aliased, big(fmt, row.oddsRatio)),
    orCell(row.aliased, interval(row.orLow, row.orHigh, fmt, strings)),
  ]);
  const head = [
    strings.colTerm,
    strings.colB,
    strings.colSe,
    strings.colZ,
    strings.colP,
    strings.colOr,
    t(strings.colOrCi, { level: levelText }),
  ];
  return (
    <div className="space-y-2">
      <h3 className={WIDGET_H3}>{strings.coefficientsTitle}</h3>
      <ResultTable head={head} rows={rows} wrapFirstColumn />
      <Explanation>{strings.orNote}</Explanation>
      <CopyButton
        copiedLabel={strings.copied}
        label={strings.copyTable}
        small
        text={[head, ...rows].map((line) => line.join("\t")).join("\n")}
      />
    </div>
  );
}

function Fit({ report, cutoff, design, event, strings, fmt }: Props) {
  const num = (value: number) => formatStat(fmt, value);
  const c = classify(design.y, report.fitted, cutoff);
  const pct = (value: number) =>
    Number.isNaN(value)
      ? "—"
      : fmt(value, { style: "percent", maximumFractionDigits: 1 });
  return (
    <div className="grid gap-6 lg:grid-cols-2">
      <div className="space-y-2">
        <h3 className={WIDGET_H3}>{strings.fitTitle}</h3>
        <ResultTable
          head={[strings.colMeasure, strings.colValue]}
          rows={[
            [strings.rowDeviance, num(report.deviance)],
            [strings.rowNullDeviance, num(report.nullDeviance)],
            [strings.rowAic, num(report.aic)],
            [strings.rowBic, num(report.bic)],
            [strings.rowCoxSnell, num(report.coxSnell)],
            [strings.rowTjur, num(report.tjur)],
            [strings.rowIterations, fmt(report.iterations)],
          ]}
          wrapFirstColumn
        />
        <Explanation>{strings.fitNote}</Explanation>
      </div>
      <div className="space-y-2">
        <h3 className={WIDGET_H3}>
          {t(strings.classTitle, {
            cutoff: fmt(cutoff, { maximumFractionDigits: 3 }),
          })}
        </h3>
        <ResultTable
          head={[
            strings.colActual,
            strings.colPredictedEvent,
            strings.colPredictedNon,
          ]}
          rows={[
            [t(strings.rowEvent, { event }), fmt(c.tp), fmt(c.fn)],
            [strings.rowNonEvent, fmt(c.fp), fmt(c.tn)],
          ]}
          wrapFirstColumn
        />
        <ResultTable
          head={[strings.colMeasure, strings.colValue]}
          rows={[
            [strings.sensitivity, pct(c.sensitivity)],
            [strings.specificity, pct(c.specificity)],
            [strings.ppv, pct(c.ppv)],
            [strings.npv, pct(c.npv)],
          ]}
          wrapFirstColumn
        />
      </div>
    </div>
  );
}

function Plots({
  report,
  design,
  termNames,
  event,
  outcomeName,
  strings,
  fmt,
}: Props) {
  const single =
    design.predictors.length === 1 && !report.coefficients[1].aliased;
  const column = design.predictors[0] ?? [];
  const low = Math.min(...column);
  const high = Math.max(...column);
  const curveX = Array.from(
    { length: CURVE_POINTS },
    (_, i) => low + ((high - low) * i) / (CURVE_POINTS - 1)
  );
  const [b0, b1] = [
    report.coefficients[0].estimate,
    report.coefficients[1].estimate,
  ];
  const curveY = curveX.map((x) => 1 / (1 + Math.exp(-(b0 + b1 * x))));
  return (
    <div className="grid gap-6 lg:grid-cols-2">
      <div className="space-y-2">
        <h3 className={WIDGET_H3}>{strings.rocTitle}</h3>
        <XYPlot
          diagonal
          fmt={fmt}
          label={t(strings.rocLabel, { auc: formatStat(fmt, report.auc) })}
          series={[
            {
              kind: "line",
              xs: report.roc.map((p) => p.fpr),
              ys: report.roc.map((p) => p.tpr),
              color: 0,
              label: `${strings.seriesRoc} (${strings.auc} ${formatStat(fmt, report.auc, 3)})`,
            },
          ]}
          xLabel={strings.axisFpr}
          xRange={[0, 1]}
          yLabel={strings.axisTpr}
          yRange={[0, 1]}
        />
      </div>
      {single && high > low && report.status !== "separation" ? (
        <div className="space-y-2">
          <h3 className={WIDGET_H3}>{strings.curveTitle}</h3>
          <XYPlot
            fmt={fmt}
            label={t(strings.curveLabel, { event, x: termNames[0] })}
            series={[
              {
                kind: "points",
                xs: column,
                ys: design.y,
                color: 1,
                label: t(strings.seriesObserved, { event }),
              },
              {
                kind: "line",
                xs: curveX,
                ys: curveY,
                color: 0,
                label: strings.seriesCurve,
              },
            ]}
            xLabel={termNames[0]}
            yLabel={`${strings.axisProbability}: ${outcomeName} = ${event}`}
            yRange={[0, 1]}
          />
        </div>
      ) : null}
    </div>
  );
}

function Predict({ report, design, termNames, strings, fmt }: Props) {
  const [values, setValues] = useState<Record<string, string>>({});
  const parsed = termNames.map((name) => parseNumberInput(values[name] ?? ""));
  const point = parsed.every((v) => v !== null)
    ? predictProbability(report, parsed as number[])
    : null;
  const fillMeans = () =>
    setValues(
      Object.fromEntries(
        termNames.map((name, j) => {
          const column = design.predictors[j];
          const mean = column.reduce((a, b) => a + b, 0) / column.length;
          return [
            name,
            fmt(mean, { maximumFractionDigits: 4, useGrouping: false }),
          ];
        })
      )
    );
  return (
    <div className={`${RESULT_CARD} space-y-4`}>
      <h3 className={WIDGET_H3}>{strings.predictTitle}</h3>
      <div className="grid gap-4 sm:grid-cols-3">
        {termNames.map((name, j) => (
          <TextField
            id={`lg-new-${j}`}
            key={name}
            label={name}
            numeric
            onChange={(value) => setValues((v) => ({ ...v, [name]: value }))}
            value={values[name] ?? ""}
          />
        ))}
      </div>
      <button className={BUTTON_SECONDARY} onClick={fillMeans} type="button">
        {strings.fillMeans}
      </button>
      {point ? (
        <div aria-live="polite">
          <StatGrid>
            <StatCard
              emphasis
              label={strings.predictedProbability}
              value={fmt(point.probability, {
                style: "percent",
                maximumFractionDigits: 1,
              })}
            />
            <StatCard
              label={strings.predictedOdds}
              value={big(fmt, point.odds)}
            />
            <StatCard
              label={t(strings.predictedInterval, {
                level: fmt(report.level, { style: "percent" }),
              })}
              value={t(strings.ciTemplate, {
                low: fmt(point.interval[0], {
                  style: "percent",
                  maximumFractionDigits: 1,
                }),
                high: fmt(point.interval[1], {
                  style: "percent",
                  maximumFractionDigits: 1,
                }),
              })}
            />
          </StatGrid>
        </div>
      ) : null}
    </div>
  );
}

function Working({ report, strings, fmt }: Props) {
  const num = (value: number) => formatStat(fmt, value);
  return (
    <WorkingPanel title={strings.workingTitle}>
      {report.firth ? (
        <Explanation>
          {t(strings.workFirth, { count: fmt(report.iterations) })}
        </Explanation>
      ) : (
        <>
          <Explanation>{strings.workIrls}</Explanation>
          <Explanation>
            {t(strings.workIterations, { count: fmt(report.iterations) })}
          </Explanation>
        </>
      )}
      <Formula>
        {report.firth
          ? t(strings.workLrFirth, {
              chi: num(report.lrChi2),
              df: fmt(report.lrDf),
            })
          : t(strings.workLr, {
              null: num(report.nullDeviance),
              dev: num(report.deviance),
              chi: num(report.lrChi2),
              df: fmt(report.lrDf),
            })}
      </Formula>
      <Formula>
        {t(strings.workNagelkerke, { r2: num(report.nagelkerke) })}
      </Formula>
    </WorkingPanel>
  );
}

export function LogisticResults(props: Props) {
  return (
    <>
      <Banners {...props} />
      <Headline {...props} />
      <Coefficients {...props} />
      <Fit {...props} />
      <Plots {...props} />
      {props.report.status === "separation" ? null : <Predict {...props} />}
      <Working {...props} />
    </>
  );
}
